import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';

import AddApp from '@/assets/icons/AddApp';
import Button from '@/components/Buttons/Button';
import { PageInformationContext } from '@/context/PageInformationContext';
import { ButtonsContainer } from '@/features/Dashboard/drawer/components/ButtonsContainer';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import type { MarketApps } from '@/features/Dashboard/types';
import { useMarketPreview } from '@/features/Markets/api';
import MarketsComponents from '@/pages/Markets/MarketsComponents';
import MarketsSkeleton from '@/pages/Markets/MarketsSkeleton';

import { DashboardLayout } from '../../components/DashboardLayout';
import { DashboardShareDialog } from '../../components/DashboardShareDialog';
import { DashboardStep } from '../../constants/DashboardStep';
import { DASHBOARD_ACTION_BUTTON_WIDTH } from '../../constants/style';

export const ViewDashboard = ({
  setStep,
  dashboardConfig,
  tabNames,
  activeTab,
  title,
  onTabChange
}: {
  setStep: Dispatch<SetStateAction<DashboardStep>>;
  dashboardConfig: MarketApps | undefined;
  tabNames: string[];
  activeTab: number;
  title: string;
  onTabChange: (index: number) => void;
}) => {
  const { data, isLoading, isFetching } = useMarketPreview(dashboardConfig);
  const { setDrawerStep } = useDrawerSteps();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { setMode } = useAppForm();

  useEffect(() => {
    setMode('create');
  }, []);

  // Show loading for content area when fetching (keeps tabs visible for optimistic switching)
  const showContentLoading = isLoading || isFetching;

  return (
    <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
      <DashboardLayout
        activeTab={activeTab}
        renderRight={
          <ButtonsContainer>
            <Button
              color='secondary'
              size='large'
              startIcon={<ShareOutlinedIcon />}
              sx={{
                width: DASHBOARD_ACTION_BUTTON_WIDTH
              }}
              variant='contained'
              onClick={() => {
                setShareDialogOpen(true);
              }}
            >
              Share
            </Button>
            <Button
              color='secondary'
              size='large'
              startIcon={<EditOutlinedIcon />}
              sx={{
                width: DASHBOARD_ACTION_BUTTON_WIDTH
              }}
              variant='contained'
              onClick={() => {
                setStep(DashboardStep.Edit);
              }}
            >
              Edit
            </Button>
            <Button
              color='secondary'
              size='large'
              startIcon={<AddApp fill='#F8F8F8' />}
              sx={{
                width: DASHBOARD_ACTION_BUTTON_WIDTH
              }}
              variant='contained'
              onClick={() => {
                setDrawerStep(DrawerStep.SelectApp);
              }}
            >
              Add app
            </Button>
          </ButtonsContainer>
        }
        tabNames={tabNames}
        title={title}
        onTabChange={onTabChange}
      >
        {showContentLoading ? (
          <MarketsSkeleton />
        ) : (
          data && (
            <MarketsComponents
              components={data.components}
              showAddToDashboard={false}
              showShareBlock={false}
            />
          )
        )}
      </DashboardLayout>
      <DashboardShareDialog
        open={shareDialogOpen}
        title={title}
        onClose={() => {
          setShareDialogOpen(false);
        }}
      />
    </PageInformationContext>
  );
};
